import { get } from 'lodash'

import { Colors, DateFields, Services, getPrice } from '@sigma-frontend-libs/commons'

import { components, getLocaleDateString, getLocaleTimeString } from '@/utils'

import copyIcon from '@/components/icon/copy'
import SingleLink from '@/components/misc/SingleLink'
import { formatPriceCalculation } from '@/components/services/billings/utils'
import { sendingsStatuses } from '@/components/services/sendings/utils'

const className = 'format-table'

function renderCopyIcon(h, value) {
  return h(
    copyIcon,
    {
      props: {
        value,
        size: 14,
        marginless: true
      }
    }
  )
}
function renderCopy(h, path, value) {
  let pathToValue = 'id'
  switch (path) {
    case 'meta._recipientData': {
      pathToValue = 'operator'
      break
    }
    // https://sigmasms.slack.com/archives/C03RRLZB8MP/p1690526629402459
    case 'Owner': {
      pathToValue = 'username'
      break
    }
  }

  let data
  switch (true) {
    case typeof value === 'object': {
      data = get(value, pathToValue)
      if (Array.isArray(value) && value.length) {
        value.map(item => {
          if (typeof item !== 'string') {
            data = item
          } else {
            data = item.split(':').at(-1)
          }
        })
      }
      break
    }
    case typeof value === 'string':
    case typeof value === 'number': {
      data = value
      break
    }
    default: {
      data = ''
      break
    }
  }

  // https://sigmasms.slack.com/archives/C03RRLZB8MP/p1690526629402459
  if (path === 'meta._routing.provider') {
    data = this.provider?.title

    if (this.loading) {
      return h(
        'div',
        {
          class: 'faic fjcc square--20'
        },
        [
          h(
            components.progress,
            {
              props: {
                indeterminate: true,
                color: Colors.primary,
                size: 10,
                width: 1
              }
            }
          )
        ]
      )
    }
  }
  if (Object.values(DateFields).includes(path)) {
    data = [ getLocaleDateString(data), getLocaleTimeString(data) ].join(' ')
  }

  return h(
    'div',
    {
      class: `${className}__row-copy square--20`
    },
    [ renderCopyIcon.call(this, h, data) ]
  )
}

function renderLabel(h, label) {
  if (label) {
    return h(
      'div',
      {
        class: `${className}__row-label`
      },
      [ label ]
    )
  }
}

function renderRouterLink(h, value, to) {
  return h(
    'router-link',
    {
      class: 'link',
      props: { to }
    },
    [ value ]
  )
}

function renderSingleLink(h, service, value) {
  return h(
    SingleLink,
    {
      props: {
        service,
        value
      }
    }
  )
}

function renderInnerHTML(h, value) {
  return h(
    'div',
    {
      domProps: {
        innerHTML: value
      }
    }
  )
}
function renderHTML(h, value) {
  return h(
    'div',
    {
      class: `${className}__row-html`
    },
    [ renderInnerHTML.call(this, h, value) ]
  )
}

function renderValueByPath(h, path, value) {
  switch (path) {
    case 'id': {
      const to = {}
      switch (this.service) {
        case Services.sendings:
        case Services.billings: {
          to.name = this.service
          to.query = { id: this.value.id }
          break
        }
        case Services.sendingsFallbacks: {
          to.name = Services.sendings
          to.query = { id: this.value.id }
          break
        }
        default: {
          to.name = `${this.service}.single`,
          to.params = { id: this.value.id }
          break
        }
      }

      return renderRouterLink.call(this, h, value, to)
    }

    case 'meta.billings.ids': {
      if (Array.isArray(value) && value.length) {
        return value.map(id => {
          return renderRouterLink.call(this, h, id, {
            name: Services.billings,
            params: { id }
          })
        })
      }
      break
    }

    case 'state.detailed.ids': {
      if (Array.isArray(value) && value.length) {
        return value.map(id => {
          if (typeof id !== 'string') {
            return id
          } else {
            return id.split(':').map((id, index) => {
              let fontWeight
              if (index === 1) {
                fontWeight = 'bold'
              }

              return h(
                'div',
                {
                  style: { fontWeight }
                },
                id
              )
            })
          }
        })
      }
      break
    }

    case 'state.status': {
      let statusService = Services.sendings
      if (this.service !== Services.sendingsFallbacks) {
        statusService = this.service
      }

      return this.getTranslate(`${statusService}.statuses.${value}`)
    }
    case 'type': return this.getTranslate(`${this.service}.types.${value}`)
    case 'channel': return this.getTranslate(`${this.service}.channels.${value}`)

    case 'source': {
      if (this.service === Services.billings) {
        const [ source, type ] = value.split('.')

        return this.getTranslate(`${Services.billings}.sources.${type || source}`)
      }
      break
    }

    case '$options': return renderInnerHTML.call(this, h, value)

    case 'title':
    case 'text':
    case 'payload.text': return h('pre', value)
    case 'notification': return renderHTML.call(this, h, value)

    case 'payload.image':
    case 'payload.file':
    case 'payload.audio':
    case 'payload.video': {
      return renderRouterLink.call(this, h, value, {
        name: 'files',
        query: { id: value }
      })
    }

    case 'dursec': return `${value} ${this.getTranslate('misc.measure.time.sec')}`
    case 'billsec': return `${value} ${this.getTranslate('misc.measure.time.sec')}`
    case 'amount':
    case 'data.price':
    case 'meta.billing.amount': return getPrice(value)

    case 'meta._recipientData': {
      const { group, operator, mcc, mnc } = value
      const result = [ group ]

      if (this.checkPermissions('advanced.operatorGroups.get')) {
        result.push(operator, [ mcc, mnc ].filter(Boolean).join(':'))
      }

      const values = result.filter(Boolean)

      if (Array.isArray(values) && values.length) {
        return values.join(' > ')
      }
      break
    }

    case 'Owner':
    case 'OwnerId':
    case 'Owner.id':
    case 'targetUserId':
    case 'targetUser': return renderSingleLink.call(this, h, Services.users, value)
    case 'TariffId': return renderSingleLink.call(this, h, Services.tariffs, value)
    case 'meta.patternId': return renderSingleLink.call(this, h, Services.patterns, value)
    case 'meta._routing.provider': return renderSingleLink.call(this, h, Services.providers, value)
    case 'isRead': return this.getTranslate(`${this.service}.isRead.${value}`)
    case 'sentAt':
    case 'deliveredAt':
    case 'createdAt':
    case 'updatedAt': return [ getLocaleDateString(value), getLocaleTimeString(value) ].join(' ')
    default: return value
  }
}
function renderValue(h, path, value) {
  return h(
    'div',
    {
      class: `${className}__row-value`
    },
    [ renderValueByPath.call(this, h, path, value) ]
  )
}
function renderItem(h, { path, value }) {
  switch (path) {
    case '$options': {
      if (this.service === Services.sendingsFallbacks) {
        const convertStatuses = statuses => {
          return statuses.map(status => {
            return `<span class="text--${sendingsStatuses[status].color}">${this.getTranslate(`${Services.sendings}.statuses.${status}`)}</span>`
          }).join(', ')
        }

        return Object.keys(value).map(key => {
          const option = value[key]
          let content
          switch (key) {
            case 'onStatus': {
              content = convertStatuses(option)
              break
            }
            case 'onTimeout': {
              content = `${this.getTranslate(`${Services.sendings}.labels.options.timeout`) + ': ' + option.timeout + ' ' + this.getTranslate('misc.measure.time.sec').toLowerCase()}`
              content += ', '
              content += `${this.getTranslate(`${Services.sendings}.labels.options.except`) + ': ' + convertStatuses(option.except)}`
              break
            }
            case 'onIncomingPayload': {
              content = option.text
              break
            }
          }

          return h(
            'div',
            {
              class: `${className}__row`
            },
            [
              renderLabel.call(this, h, this.getTranslate(`${this.service}.labels.${path}.${key}`)),
              renderCopy.call(this, h, '', content),
              renderValue.call(this, h, `${path}`, content)
            ]
          )
        })
      }
      break
    }
    case 'payload.buttons': {
      return value.map((button, index) => {
        return Object.keys(button).map(key => {
          const label = this.getTranslate(`${this.service}.labels.payload.button.${key}`)

          return h(
            'div',
            {
              class: `${className}__row`
            },
            [
              renderLabel.call(this, h, `${label} ${index + 1}`),
              renderCopy.call(this, h, '', button[key]),
              renderValue.call(this, h, path, button[key])
            ]
          )
        })
      })
    }
    case 'data.priceCalculation': {
      return h(
        'div',
        {
          class: `${className}__row`
        },
        [
          renderLabel.call(this, h, this.getTranslate(`${this.service}.labels.priceCalculation`)),
          renderCopy.call(this, h, path, formatPriceCalculation.call(this, value)),
          renderInnerHTML.call(this, h, formatPriceCalculation.call(this, value, '<br>'))
        ]
      )
    }
    default: {
      return h(
        'div',
        {
          class: `${className}__row`
        },
        [
          renderLabel.call(this, h, this.getTranslate(`${this.service}.labels.${path}`)),
          renderCopy.call(this, h, path, value),
          renderValue.call(this, h, path, value)
        ]
      )
    }
  }
}

function renderItems(h) {
  if (this.items) {
    if (Array.isArray(this.items) && this.items.length) {
      return this.items.map(item => {
        return renderItem.call(this, h, item)
      })
    }
  }
}

export default function(h) {
  return h(
    'div',
    {
      class: className
    },
    [ renderItems.call(this, h) ]
  )
}
